import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "@/lib/useLenis";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Grain from "@/components/Grain";
import Reveal from "@/components/Reveal";
import RSVPCard from "@/components/RSVPCard";

const ATHENA_IMG = "/media/athena.webp";

/* --------------------------- Event hero -------------------------- */
function EventHero() {
  const wrapRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: wrapRef,
    offset: ["start end", "end start"],
  });
  const ghostY = useTransform(scrollYProgress, [0, 1], ["0%", "-20%"]);
  const ghostO = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.7, 0.35]);

  const headline = [
    { text: "Bubbles, Big Ideas", italic: false, grad: false },
    { text: "& Breakthroughs.", italic: true, grad: true },
  ];

  return (
    <section
      id="top"
      ref={wrapRef}
      className="relative min-h-[82vh] md:min-h-[82vh] flex flex-col justify-end overflow-hidden"
    >
      <div className="hero-athena" aria-hidden="true">
        <div className="hero-athena__img" style={{ backgroundImage: `url(${ATHENA_IMG})` }} />
        <div className="hero-athena__ember" />
        <div className="hero-athena__fade" />
      </div>

      <motion.div style={{ y: ghostY, opacity: ghostO }} className="ghost-word" aria-hidden="true">
        EVENT
      </motion.div>

      <div className="container-editorial relative z-10 event-mobile-pt grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-9 lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="overline mb-4 md:mb-5"
          >
            Women in Dentistry Investor Social &mdash; Invite Only &mdash; Chicago
          </motion.div>

          <h1 className="hero-h1" style={{ fontSize: "clamp(38px, 5.6vw, 96px)" }}>
            {headline.map((line, idx) => (
              <span className="mask-line" key={idx}>
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{
                    duration: 1.2,
                    ease: [0.76, 0, 0.24, 1],
                    delay: 0.25 + idx * 0.14,
                  }}
                  className={line.italic ? "italic-serif" : ""}
                  style={line.grad ? { display: "inline-block" } : { display: "block" }}
                >
                  {line.grad ? <span className="grad-text">{line.text}</span> : line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 0.9 }}
            className="mt-4 md:mt-6 italic-serif"
            style={{ fontSize: "clamp(20px, 2.2vw, 28px)", color: "var(--stone)" }}
          >
            Heading to Chicago in February? Join Cara Capital, the Cara Council, and Women in Dentistry for an evening of bubbly, great conversation, and the future of oral health.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.0, ease: [0.22, 1, 0.36, 1], delay: 1.0 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="#rsvp"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("rsvp");
              }}
              className="btn btn-ember"
              data-testid="event-hero-rsvp"
            >
              RSVP for the event →
            </a>
            <a
              href="#details"
              onClick={(e) => {
                e.preventDefault();
                scrollToId("details");
              }}
              className="btn btn-outline"
              data-testid="event-hero-details"
            >
              Event details
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Event story -------------------------- */
function Story() {
  return (
    <section id="details" className="section hairline-t">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">About the Evening</div>
          </Reveal>
        </div>
        <div className="md:col-span-9 max-w-4xl">
          <Reveal delay={0.05}>
            <h2
              className="font-serif"
              style={{
                fontSize: "clamp(30px, 3.6vw, 44px)",
                lineHeight: 1.15,
                letterSpacing: "-0.01em",
              }}
            >
              The Great Wealth Transfer is underway.{" "}
              <span className="italic-serif grad-text">
                Care convergence is where women will shape what comes next.
              </span>
            </h2>
          </Reveal>

          <Reveal delay={0.12}>
            <p
              className="mt-8 text-[16.5px] md:text-[17.5px]"
              style={{ color: "var(--ink)", lineHeight: 1.7 }}
            >
              We are bringing together women who are investing in care convergence and curious about venture investing in oral health. The care convergence thesis is reshaping oral health from a standalone specialty into the innovation opportunity of the decade &mdash; and this is the room where those conversations happen.
            </p>
          </Reveal>

          <Reveal delay={0.18}>
            <p
              className="mt-6 text-[16.5px] md:text-[17.5px]"
              style={{ color: "var(--ink)", lineHeight: 1.7 }}
            >
              Whether you are a seasoned investor or just exploring, come connect, swap ideas, and meet the women shaping what is next in dentistry. Perfectly timed after the Dental VIP Summit and before the Chicago Midwinter Meeting kicks off.
            </p>
          </Reveal>

          <Reveal delay={0.24}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 border" style={{ borderColor: "var(--vein)" }}>
              {[
                { k: "Education", v: "A framing conversation on care convergence, investing, and how wealth creates influence." },
                { k: "Inspiration", v: "Women founders, clinicians, and investors on stage \u2014 building what comes next in oral health." },
                { k: "Connection", v: "A small, intentional gathering. Happy hour with wine, champagne, beer, sodas, and appetizers." },
              ].map((it, i) => (
                <div
                  key={it.k}
                  className={`p-6 md:p-8 ${i > 0 ? "md:hairline-l" : ""} ${i > 0 ? "border-t md:border-t-0" : ""}`}
                  style={{ borderColor: "var(--vein)" }}
                >
                  <div className="microlabel">Pillar {String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-3 font-serif" style={{ fontSize: 22, letterSpacing: "-0.01em" }}>
                    {it.k}
                  </div>
                  <p className="mt-3 text-[14.5px]" style={{ color: "var(--stone)", lineHeight: 1.6 }}>
                    {it.v}
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- Detail rows -------------------------- */
function Details() {
  const rows = [
    ["When", "Wednesday, February 24, 2027 \u00b7 5:30 \u2013 7:30 PM"],
    ["Where", "Level Two at Hyatt Centric Chicago Magnificent Mile \u00b7 633 N St. Clair St., Chicago, IL 60611"],
    [
      "Timing",
      <>
        Perfectly timed after the{" "}
        <a
          href="https://www.dentalvipsummit.com"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "var(--ember)", textDecoration: "underline", textUnderlineOffset: "3px" }}
          data-testid="event-vip-summit-link"
        >
          Dental VIP Summit
        </a>{" "}
        and before the Chicago Midwinter Meeting kicks off. No conference registration needed to attend.
      </>,
    ],
    ["Setting", "Hyatt hotel bar \u2014 not a private room, so expect a lively, open atmosphere. It should not be too crowded at this time."],
    ["Food & Drink", "Wine, champagne, beer, sodas, and appetizers provided. Guests who\u2019d like cocktails or liquor can purchase their own."],
    ["Attire", "Casual professional."],
    ["RSVP", "Spots are limited, so RSVP today. Limit one guest per RSVP \u2014 each guest must RSVP individually."],
  ];
  return (
    <section className="section hairline-t">
      <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Reveal>
            <div className="overline">At a Glance</div>
          </Reveal>
        </div>
        <div className="md:col-span-9">
          <div className="divide-hairline hairline-t hairline-b">
            {rows.map(([k, v]) => (
              <Reveal key={k}>
                <div className="py-5 grid grid-cols-12 gap-4">
                  <div className="col-span-4 md:col-span-3 overline">{k}</div>
                  <div
                    className="col-span-8 md:col-span-9 text-[15.5px]"
                    style={{ color: "var(--ink)" }}
                  >
                    {v}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------------- RSVP section ------------------------- */
function RsvpSection() {
  return (
    <section className="relative section hairline-t overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          backgroundImage: `url(${ATHENA_IMG})`,
          backgroundSize: "cover",
          backgroundPosition: "center 15%",
          filter: "grayscale(100%)",
          opacity: 0.16,
        }}
      />
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            "linear-gradient(180deg, rgba(248,245,240,0.85), rgba(248,245,240,0.92) 60%, var(--marble))",
        }}
      />
      <div className="container-editorial relative grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <Reveal>
            <div className="overline">Request Your Seat</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="mt-6 font-serif"
              style={{
                fontSize: "clamp(34px, 4.2vw, 56px)",
                lineHeight: 1.03,
                letterSpacing: "-0.02em",
              }}
            >
              A small room.{" "}
              <span className="italic-serif grad-text">A signal-rich evening.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              className="mt-6 max-w-md text-[15.5px]"
              style={{ color: "var(--stone)", lineHeight: 1.65 }}
            >
              Reserve your spot below. Limit one guest per RSVP &mdash; all guests must RSVP individually. The Cara team will follow up with a confirmation.
            </p>
          </Reveal>
        </div>
        <div className="md:col-span-7">
          <RSVPCard />
        </div>
      </div>
    </section>
  );
}

function HashScroll() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const t = setTimeout(() => scrollToId(id), 300);
    return () => clearTimeout(t);
  }, [location.hash]);
  return null;
}

export default function Event() {
  useEffect(() => {
    document.title = "Women in Dentistry Investor Social \u2014 Cara Capital, Chicago, Feb 24, 2027";
  }, []);
  return (
    <div className="bg-marble" style={{ position: "relative" }}>
      <Grain />
      <Nav current="event" />
      <HashScroll />
      <EventHero />
      <Story />
      <Details />
      <RsvpSection />
      <Footer />
    </div>
  );
}
